import { useNavigate } from 'react-router-dom';
import { ChevronRight, CheckCircle, BookOpen, Bookmark } from 'lucide-react';
import { ScrollArea } from '../components/ui/scroll-area';
import { useCourse } from '../context/CourseContext';
import { cn } from '../lib/utils';

export default function Sidebar({ sections, currentSectionId, currentSubtopicId, onSubtopicClick, className }) {
  const navigate = useNavigate();
  const { isSubtopicComplete, isQuizComplete, getSectionProgress, isBookmarked } = useCourse();

  return (
    <div className={cn("bg-card border border-border rounded-xl", className)} data-testid="course-sidebar">
      <div className="p-4 border-b border-border">
        <h2 className="font-heading font-semibold text-sm uppercase tracking-widest text-muted-foreground">
          Course Sections
        </h2>
      </div>
      <ScrollArea className="h-[calc(100vh-12rem)]">
        <nav className="p-2" data-testid="sidebar-nav">
          {sections.map((section) => {
            const isCurrentSection = section.id === currentSectionId;
            const subtopicIds = section.subtopics?.map(st => st.id) || [];
            const sectionProgress = getSectionProgress(subtopicIds);
            const quizDone = isQuizComplete(section.id);

            return (
              <div key={section.id} className="mb-1" data-testid={`sidebar-section-${section.id}`}>
                <button
                  onClick={() => navigate(`/section/${section.id}`)}
                  className={cn(
                    "w-full text-left px-3 py-2.5 rounded-lg flex items-start gap-2.5 sidebar-link transition-all duration-150",
                    isCurrentSection
                      ? "bg-sf-blue-light border-l-[3px] border-l-sf-blue"
                      : "border-l-[3px] border-l-transparent hover:bg-secondary"
                  )}
                  data-testid={`sidebar-section-btn-${section.id}`}
                >
                  <span className={cn(
                    "flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold mt-0.5",
                    sectionProgress === 100 && quizDone
                      ? "bg-sf-success text-white"
                      : isCurrentSection
                        ? "bg-sf-blue text-white"
                        : "bg-secondary text-muted-foreground"
                  )}>
                    {sectionProgress === 100 && quizDone ? (
                      <CheckCircle className="h-3.5 w-3.5" />
                    ) : (
                      section.id
                    )}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className={cn(
                      "text-sm leading-snug",
                      isCurrentSection ? "font-semibold text-foreground" : "text-muted-foreground"
                    )}>
                      {section.title}
                    </p>
                    {sectionProgress > 0 && sectionProgress < 100 && (
                      <div className="mt-1.5 h-1 bg-secondary rounded-full overflow-hidden">
                        <div className="h-full bg-sf-blue rounded-full transition-all duration-300" style={{ width: `${sectionProgress}%` }} />
                      </div>
                    )}
                  </div>
                </button>

                {isCurrentSection && section.subtopics && (
                  <div className="ml-8 mt-1 space-y-0.5 pb-1">
                    {section.subtopics.map((st) => {
                      const isComplete = isSubtopicComplete(st.id);
                      const isActive = st.id === currentSubtopicId;
                      return (
                        <button
                          key={st.id}
                          onClick={() => onSubtopicClick?.(st.id)}
                          className={cn(
                            "w-full text-left px-2.5 py-1.5 rounded-md text-xs flex items-center gap-2 transition-all duration-150",
                            isActive
                              ? "bg-sf-blue-light text-sf-blue font-medium"
                              : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                          )}
                          data-testid={`sidebar-subtopic-${st.id}`}
                        >
                          {isComplete ? (
                            <CheckCircle className="h-3 w-3 text-sf-success flex-shrink-0" />
                          ) : (
                            <ChevronRight className="h-3 w-3 flex-shrink-0" />
                          )}
                          <span className="truncate flex-1">{st.title}</span>
                          {isBookmarked(st.id) && (
                            <Bookmark className="h-3 w-3 text-sf-warning fill-sf-warning flex-shrink-0" />
                          )}
                        </button>
                      );
                    })}
                    <button
                      onClick={() => onSubtopicClick?.('quiz')}
                      className={cn(
                        "w-full text-left px-2.5 py-1.5 rounded-md text-xs flex items-center gap-2 transition-all duration-150",
                        currentSubtopicId === 'quiz'
                          ? "bg-sf-blue-light text-sf-blue font-medium"
                          : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                      )}
                      data-testid={`sidebar-quiz-${currentSectionId}`}
                    >
                      {quizDone ? (
                        <CheckCircle className="h-3 w-3 text-sf-success flex-shrink-0" />
                      ) : (
                        <BookOpen className="h-3 w-3 flex-shrink-0" />
                      )}
                      <span>Section Quiz</span>
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </ScrollArea>
    </div>
  );
}
